import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AppLayout from '../components/AppLayout';
import toast from 'react-hot-toast';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  FolderIcon,
  FolderOpenIcon
} from '@heroicons/react/24/outline';

interface Category {
  _id: string;
  type: 'main' | 'sub' | 'detail';
  code: string;
  name: string;
  productCodeParam?: string;
  isActive: boolean;
  sortOrder: number;
}

const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    type: 'main' as 'main' | 'sub' | 'detail',
    code: '',
    name: '',
    productCodeParam: '',
    sortOrder: 0
  });

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSeedCategories = async () => {
    try {
      const response = await axios.post('/categories/seed');
      toast.success(`${response.data.created} kategori eklendi, ${response.data.skipped} atlandı`);
      loadCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Kategoriler yüklenemedi!');
    }
  };

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      // Flat liste olarak al
      const response = await axios.get('/categories/flat');
      
      // Eğer hiç kategori yoksa, otomatik olarak seed yap
      if (!response.data || response.data.length === 0) {
        toast.loading('Varsayılan kategoriler yükleniyor...', { id: 'auto-seed' });
        try {
          const seedResponse = await axios.post('/categories/seed');
          toast.success(
            `${seedResponse.data.created} kategori otomatik yüklendi!`, 
            { id: 'auto-seed' }
          );
          // Kategorileri tekrar yükle
          const newResponse = await axios.get('/categories/flat');
          setCategories(newResponse.data);
        } catch (seedError) {
          toast.error('Kategoriler yüklenemedi!', { id: 'auto-seed' });
        }
      } else {
        setCategories(response.data);
      }
    } catch (error) {
      toast.error('Kategoriler yüklenemedi!');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingCategory) {
        await axios.put(`/categories/${editingCategory._id}`, formData);
        toast.success('Kategori güncellendi!');
      } else {
        await axios.post('/categories', formData);
        toast.success('Kategori oluşturuldu!');
      }
      
      setShowModal(false);
      setEditingCategory(null);
      resetForm();
      loadCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız!');
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      try {
        await axios.delete(`/categories/${categoryId}`);
        toast.success('Kategori silindi!');
        loadCategories();
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Kategori silinemedi!');
      }
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      type: category.type,
      code: category.code,
      name: category.name,
      productCodeParam: category.productCodeParam || '',
      sortOrder: category.sortOrder
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      type: 'main',
      code: '',
      name: '',
      productCodeParam: '',
      sortOrder: 0
    });
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'main': return 'Ana Grup';
      case 'sub': return 'Alt Grup';
      case 'detail': return 'Detay Grup';
      default: return type;
    }
  };

  const countCategories = () => {
    const mainCount = categories.filter(c => c.type === 'main').length;
    const subCount = categories.filter(c => c.type === 'sub').length;
    const detailCount = categories.filter(c => c.type === 'detail').length;
    return { mainCount, subCount, detailCount };
  };

  const stats = countCategories();

  return (
    <AppLayout title="Kategori Yönetimi" subtitle="Ana Grup, Alt Grup ve Detay Grup Yönetimi">
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-sm hover-lift">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <FolderIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.mainCount}</p>
            <p className="text-sm text-gray-500">Ana Grup</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm hover-lift">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <FolderOpenIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.subCount}</p>
            <p className="text-sm text-gray-500">Alt Grup</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm hover-lift">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <FolderIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.detailCount}</p>
            <p className="text-sm text-gray-500">Detay Grup</p>
          </div>
        </div>

        {/* Ana Grup Tablosu */}
        <div className="bg-white rounded-2xl shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <FolderIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Ana Grup</h3>
                <p className="text-sm text-gray-500">{stats.mainCount} kategori</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingCategory(null);
                resetForm();
                setFormData(prev => ({ ...prev, type: 'main' }));
                setShowModal(true);
              }}
              className="px-4 py-2 gradient-purple text-white font-medium rounded-lg hover:shadow-lg transition-all duration-300 flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Yeni Ana Grup
            </button>
          </div>
          
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : categories.filter(c => c.type === 'main').length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Henüz ana grup eklenmemiş
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kod</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İsim</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sıra</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories
                    .filter(c => c.type === 'main')
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(cat => (
                      <tr key={cat._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono font-medium text-gray-900">{cat.code}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">{cat.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500">{cat.sortOrder}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${cat.isActive ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                            {cat.isActive ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEdit(cat)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            <PencilIcon className="w-5 h-5 inline" />
                          </button>
                          <button
                            onClick={() => handleDelete(cat._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="w-5 h-5 inline" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Alt Grup Tablosu */}
        <div className="bg-white rounded-2xl shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FolderOpenIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Alt Grup</h3>
                <p className="text-sm text-gray-500">{stats.subCount} kategori</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingCategory(null);
                resetForm();
                setFormData(prev => ({ ...prev, type: 'sub' }));
                setShowModal(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 hover:shadow-lg transition-all duration-300 flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Yeni Alt Grup
            </button>
          </div>
          
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : categories.filter(c => c.type === 'sub').length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Henüz alt grup eklenmemiş
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kod</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İsim</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Param</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sıra</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories
                    .filter(c => c.type === 'sub')
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(cat => (
                      <tr key={cat._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono font-medium text-gray-900">{cat.code}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">{cat.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500">{cat.productCodeParam || '-'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500">{cat.sortOrder}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${cat.isActive ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                            {cat.isActive ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEdit(cat)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            <PencilIcon className="w-5 h-5 inline" />
                          </button>
                          <button
                            onClick={() => handleDelete(cat._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="w-5 h-5 inline" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Detay Grup Tablosu */}
        <div className="bg-white rounded-2xl shadow-sm">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <FolderIcon className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Detay Grup</h3>
                <p className="text-sm text-gray-500">{stats.detailCount} kategori</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingCategory(null);
                resetForm();
                setFormData(prev => ({ ...prev, type: 'detail' }));
                setShowModal(true);
              }}
              className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 hover:shadow-lg transition-all duration-300 flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Yeni Detay Grup
            </button>
          </div>
          
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
              </div>
            ) : categories.filter(c => c.type === 'detail').length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Henüz detay grup eklenmemiş
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kod</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İsim</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sıra</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories
                    .filter(c => c.type === 'detail')
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(cat => (
                      <tr key={cat._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono font-medium text-gray-900">{cat.code}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">{cat.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500">{cat.sortOrder}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${cat.isActive ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                            {cat.isActive ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEdit(cat)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            <PencilIcon className="w-5 h-5 inline" />
                          </button>
                          <button
                            onClick={() => handleDelete(cat._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="w-5 h-5 inline" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Hiç kategori yoksa seed butonu */}
        {!loading && categories.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-12">
            <div className="text-center">
              <FolderIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Henüz kategori eklenmemiş</p>
              <button
                onClick={handleSeedCategories}
                className="px-6 py-3 gradient-purple text-white font-medium rounded-lg hover:shadow-lg transition-all duration-300"
              >
                Varsayılan Kategorileri Yükle
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Category Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              {editingCategory ? 'Kategori Düzenle' : `Yeni ${getTypeLabel(formData.type)}`}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tip *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value as any})}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="main">Ana Grup</option>
                  <option value="sub">Alt Grup</option>
                  <option value="detail">Detay Grup</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kod *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">İsim *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {formData.type === 'sub' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ürün Kodu Parametresi</label>
                  <input
                    type="text"
                    value={formData.productCodeParam}
                    onChange={(e) => setFormData({...formData, productCodeParam: e.target.value})}
                    placeholder="örn: 01, 002, 003"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sıralama</label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({...formData, sortOrder: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="flex gap-3 pt-6">
                <button
                  type="submit"
                  className="flex-1 gradient-purple text-white font-semibold py-3 px-4 rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  {editingCategory ? 'Güncelle' : 'Kaydet'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-xl hover:bg-gray-300 transition-colors"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default CategoryManagement;
